"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, type AuthUser } from "@/lib/auth-context";
import { useGoogleLogin } from "@react-oauth/google";

function TelegramIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" className="shrink-0" fill="#229ED9">
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248-1.97 9.281c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.94z"/>
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" className="shrink-0">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

const STEPS = [
  { n: 1, label: "Account" },
  { n: 2, label: "Profile" },
  { n: 3, label: "Wallet"  },
  { n: 4, label: "Ready"   },
];

const SOCIAL_PLATFORMS = [
  { key: "x",         label: "X / Twitter", placeholder: "https://x.com/yourhandle"         },
  { key: "instagram", label: "Instagram",   placeholder: "https://instagram.com/yourhandle"  },
  { key: "tiktok",    label: "TikTok",      placeholder: "https://tiktok.com/@yourhandle"    },
  { key: "youtube",   label: "YouTube",     placeholder: "https://youtube.com/@yourchannel"  },
  { key: "telegram",  label: "Telegram",    placeholder: "https://t.me/yourhandle"           },
  { key: "discord",   label: "Discord",     placeholder: "https://discord.gg/yourserver"     },
];

type Step = 0 | 1 | 2 | 3;

export interface OnboardingFlowProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

export default function OnboardingFlow({ open, onClose, onComplete }: OnboardingFlowProps) {
  const { user, signIn } = useAuth();

  const [step, setStep] = useState<Step>(0);
  const [dir,  setDir]  = useState(1);

  /* Scroll lock */
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      document.body.style.overflow = prev;
      document.body.style.touchAction = "";
    };
  }, [open]);

  /* Auth */
  const [tgLoading, setTgLoading] = useState(false);
  const [gLoading,  setGLoading]  = useState(false);

  const googleLogin = useGoogleLogin({
    onSuccess: async tokenResponse => {
      try {
        const res  = await fetch("/api/auth/google", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ accessToken: tokenResponse.access_token }),
        });
        const data = await res.json();
        if (data.id) {
          const authUser: AuthUser = { id: data.id, name: data.name, email: data.email, avatar: data.avatar, provider: "google" };
          setProfileName(data.name || "");
          setProfilePhoto(data.avatar || "");
          signIn(authUser);
        }
      } catch {}
      setGLoading(false);
    },
    onError: () => setGLoading(false),
  });

  /* Profile */
  const [profileName,  setProfileName]  = useState("");
  const [profilePhoto, setProfilePhoto] = useState("");
  const [socialLinks,  setSocialLinks]  = useState<Record<string, string>>({});
  const [savingProfile, setSavingProfile] = useState(false);

  const filledLinks = Object.values(socialLinks).filter(v => v.trim()).length;
  const canProceed  = profileName.trim().length > 0 && filledLinks >= 2;

  /* Deposit */
  const [walletAddr,  setWalletAddr]  = useState<string | null>(null);
  const [addrLoading, setAddrLoading] = useState(false);
  const [copied,      setCopied]      = useState(false);

  /* On open: compute starting step */
  useEffect(() => {
    if (!open) return;
    if (!user) { setStep(0); return; }
    setStep(2);
    localStorage.setItem("blackdoor_onboarding_started_v1", "1");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Auto-advance from step 0 when user signs in — go to profile step */
  useEffect(() => {
    if (!open || step !== 0 || !user) return;
    setProfileName(user.name || "");
    setProfilePhoto(user.avatar || "");
    advance(1);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Fetch wallet when entering wallet step */
  useEffect(() => {
    if (step !== 2 || !user?.id || walletAddr || addrLoading) return;
    setAddrLoading(true);
    fetch(`/api/wallet?userId=${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => { if (d.address) setWalletAddr(d.address); })
      .catch(() => {})
      .finally(() => setAddrLoading(false));
  }, [step, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function advance(to?: Step) {
    setDir(1);
    setStep(prev => (to !== undefined ? to : Math.min(prev + 1, 3)) as Step);
  }
  function back() {
    setDir(-1);
    setStep(prev => Math.max(prev - 1, 0) as Step);
  }

  /* Telegram login */
  async function handleTelegram() {
    setTgLoading(true);
    try {
      const cfg = await fetch("/api/auth/telegram-config").then(r => r.json());
      if (!cfg.configured) { setTgLoading(false); return; }
      const popup = window.open(
        `https://oauth.telegram.org/auth?bot_id=${cfg.botId}&origin=${encodeURIComponent(window.location.origin)}&embed=1&request_access=write`,
        "tg_login", "width=550,height=470,resizable=yes",
      );
      if (!popup) { setTgLoading(false); return; }
      const handler = (e: MessageEvent) => {
        if (e.origin !== "https://oauth.telegram.org" || e.data?.event !== "auth_result") return;
        window.removeEventListener("message", handler);
        clearInterval(poll);
        popup.close();
        const td = e.data.result;
        if (td?.id) {
          const authUser: AuthUser = {
            id:       String(td.id),
            name:     [td.first_name, td.last_name].filter(Boolean).join(" "),
            email:    td.username ? `@${td.username}` : String(td.id),
            avatar:   td.photo_url,
            provider: "telegram",
          };
          setProfileName(authUser.name || "");
          setProfilePhoto(authUser.avatar || "");
          signIn(authUser);
        }
        setTgLoading(false);
      };
      window.addEventListener("message", handler);
      const poll = setInterval(() => {
        if (popup.closed) { clearInterval(poll); window.removeEventListener("message", handler); setTgLoading(false); }
      }, 500);
    } catch { setTgLoading(false); }
  }

  /* Save profile + advance */
  async function saveProfile() {
    if (!user?.id || !canProceed) return;
    setSavingProfile(true);
    const name = profileName.trim();
    if (name) localStorage.setItem("blackdoor_username_v1", name);
    if (profilePhoto) localStorage.setItem("blackdoor_avatar_v1", profilePhoto);
    const clean: Record<string, string> = {};
    for (const [k, v] of Object.entries(socialLinks)) { if (v.trim()) clean[k] = v.trim(); }
    await fetch("/api/user/profile", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId:      user.id,
        username:    name || undefined,
        avatarUrl:   profilePhoto || undefined,
        socialLinks: Object.keys(clean).length > 0 ? clean : undefined,
      }),
    }).catch(() => {});
    setSavingProfile(false);
    advance();
  }

  function copyAddr() {
    if (!walletAddr) return;
    navigator.clipboard.writeText(walletAddr).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {});
  }

  function handleDone() {
    localStorage.setItem("blackdoor_onboarded_v1", "1");
    onComplete?.();
    onClose();
  }

  if (!open) return null;

  const variants = {
    enter:  (d: number) => ({ x: d > 0 ? 40 : -40, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit:   (d: number) => ({ x: d > 0 ? -40 : 40, opacity: 0 }),
  };

  return (
    <div className="fixed inset-0 z-100 flex items-end md:items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/75 backdrop-blur-sm"
        onClick={onClose}
      />

      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ type: "spring", stiffness: 340, damping: 32 }}
        className="relative z-10 w-full md:max-w-md rounded-t-3xl md:rounded-3xl overflow-y-auto overscroll-contain"
        style={{ background: "#07070f", border: "1px solid rgba(255,255,255,0.08)", maxHeight: "92dvh", WebkitOverflowScrolling: "touch" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <button
            onClick={step > 0 ? back : onClose}
            className="h-8 w-8 flex items-center justify-center rounded-full border border-white/10 text-white/30 hover:text-white/60 hover:border-white/25 transition-all text-sm">
            {step > 0
              ? <svg width="7" height="12" viewBox="0 0 7 12" fill="none"><path d="M6 1L1 6l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
              : <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            }
          </button>

          <div className="flex items-center gap-1.5">
            {STEPS.map((s, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-black transition-all duration-300 ${
                  i < step   ? "bg-emerald-500 text-black"
                  : i === step ? "bg-white text-black"
                  : "bg-white/8 text-white/20"
                }`}>
                  {i < step
                    ? <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 4l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    : s.n}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`h-px w-4 transition-all duration-300 ${i < step ? "bg-emerald-500/60" : "bg-white/10"}`} />
                )}
              </div>
            ))}
          </div>

          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center rounded-full border border-white/10 text-white/30 hover:text-white/60 hover:border-white/25 transition-all">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>
        </div>

        <div className="px-5 pb-1">
          <p className="text-[9px] font-black uppercase tracking-[0.2em] text-white/20">{STEPS[step].label}</p>
        </div>

        <div className="overflow-hidden">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={step}
              custom={dir}
              variants={variants}
              initial="enter" animate="center" exit="exit"
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="px-5 pb-6 pt-2"
            >

              {/* ── STEP 0: AUTH ── */}
              {step === 0 && (
                <div className="space-y-4">
                  <div className="text-center py-2">
                    <div className="mx-auto mb-4 h-14 w-14 rounded-2xl overflow-hidden border border-white/10">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src="/logo.jpeg" alt="BlackDoor" className="w-full h-full object-cover" />
                    </div>
                    <h2 className="text-2xl font-black text-white tracking-tight">Create your account</h2>
                    <p className="text-sm text-white/35 mt-1">Join BlackDoor — all accounts are public by default</p>
                  </div>

                  <button onClick={handleTelegram} disabled={tgLoading}
                    className="w-full h-13 flex items-center gap-3 px-4 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/9 hover:border-white/20 transition-all active:scale-[0.98] disabled:opacity-50">
                    {tgLoading
                      ? <div className="h-4 w-4 rounded-full border-2 border-white/20 border-t-white/60 animate-spin shrink-0" />
                      : <TelegramIcon />}
                    <span className="text-[14px] font-semibold text-white/80">{tgLoading ? "Opening Telegram…" : "Continue with Telegram"}</span>
                  </button>

                  <button onClick={() => { setGLoading(true); googleLogin(); }} disabled={gLoading}
                    className="w-full h-13 flex items-center gap-3 px-4 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/9 hover:border-white/20 transition-all active:scale-[0.98] disabled:opacity-50">
                    {gLoading
                      ? <div className="h-4 w-4 rounded-full border-2 border-white/20 border-t-white/60 animate-spin shrink-0" />
                      : <GoogleIcon />}
                    <span className="text-[14px] font-semibold text-white/80">{gLoading ? "Opening Google…" : "Continue with Google"}</span>
                  </button>

                  <p className="text-center text-[10px] text-white/18 leading-relaxed">
                    By continuing you agree to our{" "}
                    <a href="/terms" className="underline underline-offset-2 hover:text-white/40 transition-colors">Terms</a>
                    {" "}and{" "}
                    <a href="/privacy" className="underline underline-offset-2 hover:text-white/40 transition-colors">Privacy Policy</a>
                  </p>
                </div>
              )}

              {/* ── STEP 1: PROFILE ── */}
              {step === 1 && (
                <div className="space-y-4">
                  <div>
                    <h2 className="text-2xl font-black text-white tracking-tight">Set up your profile</h2>
                    <p className="text-sm text-white/35 mt-1">Your profile will be visible to everyone on BlackDoor</p>
                  </div>

                  {/* Avatar + photo URL */}
                  <div className="flex items-start gap-3">
                    <div className="h-14 w-14 rounded-2xl bg-white/5 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
                      {profilePhoto
                        ? /* eslint-disable-next-line @next/next/no-img-element */ <img src={profilePhoto} alt="" className="w-full h-full object-cover" onError={() => setProfilePhoto("")} />
                        : <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke="white" strokeWidth="1.5" strokeOpacity="0.3"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="white" strokeWidth="1.5" strokeOpacity="0.3" strokeLinecap="round"/></svg>
                      }
                    </div>
                    <div className="flex-1 space-y-1.5">
                      <p className="text-[10px] font-black uppercase tracking-widest text-white/25">Profile Photo URL</p>
                      <input
                        type="url"
                        value={profilePhoto}
                        onChange={e => setProfilePhoto(e.target.value)}
                        placeholder="https://example.com/photo.jpg"
                        className="w-full rounded-xl border border-white/10 bg-white/5 text-white/70 text-[12px] px-3 py-2 placeholder:text-white/18 focus:outline-none focus:border-white/25"
                      />
                      <p className="text-[9px] text-white/18">Paste a link to your profile photo</p>
                    </div>
                  </div>

                  {/* Username */}
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-white/25">Username</p>
                    <input
                      type="text"
                      value={profileName}
                      onChange={e => setProfileName(e.target.value)}
                      placeholder="Your display name"
                      maxLength={32}
                      className="w-full rounded-xl border border-white/10 bg-white/5 text-white/70 text-[13px] px-3 py-2.5 placeholder:text-white/18 focus:outline-none focus:border-white/25"
                    />
                  </div>

                  {/* Social links */}
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-black uppercase tracking-widest text-white/25">Social Accounts</p>
                      <p className="text-[9px] text-white/20">
                        {filledLinks} added · <span className={filledLinks >= 2 ? "text-emerald-400" : "text-amber-400/60"}>{filledLinks >= 2 ? "requirement met" : `need ${2 - filledLinks} more`}</span>
                      </p>
                    </div>
                    <div className="space-y-2">
                      {SOCIAL_PLATFORMS.map(p => (
                        <div key={p.key} className="flex items-center gap-2">
                          <span className="w-[72px] shrink-0 text-[10px] text-white/30 truncate">{p.label}</span>
                          <input
                            type="url"
                            value={socialLinks[p.key] ?? ""}
                            onChange={e => setSocialLinks(prev => ({ ...prev, [p.key]: e.target.value }))}
                            placeholder={p.placeholder}
                            className="flex-1 rounded-xl border border-white/8 bg-white/4 text-white/70 text-[11px] px-3 py-2 placeholder:text-white/12 focus:outline-none focus:border-white/20"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <motion.button
                    onClick={saveProfile}
                    disabled={!canProceed || savingProfile}
                    whileHover={canProceed ? { scale: 1.015 } : {}}
                    whileTap={canProceed ? { scale: 0.975 } : {}}
                    className={`w-full h-13 rounded-2xl text-[14px] font-black uppercase tracking-[0.08em] transition-all ${
                      canProceed && !savingProfile
                        ? "bg-emerald-500 text-black"
                        : "bg-white/5 text-white/20 cursor-not-allowed border border-white/8"
                    }`}>
                    {savingProfile ? "Saving…" : canProceed ? "Continue" : `Add ${Math.max(0, 2 - filledLinks)} more social account${(2 - filledLinks) !== 1 ? "s" : ""}`}
                  </motion.button>

                  <button onClick={() => advance()} className="w-full text-center text-[11px] text-white/20 hover:text-white/40 transition-colors py-1">
                    Skip for now
                  </button>
                </div>
              )}

              {/* ── STEP 2: WALLET ── */}
              {step === 2 && (
                <div className="space-y-4">
                  <div>
                    <h2 className="text-2xl font-black text-white tracking-tight">Your crypto wallet</h2>
                    <p className="text-sm text-white/35 mt-1">We created a personal Arbitrum One wallet for you</p>
                  </div>

                  <div className="rounded-2xl border border-white/8 bg-white/3 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-black uppercase tracking-widest text-white/25">Your Wallet Address</p>
                      <span className="text-[9px] font-black uppercase tracking-widest text-emerald-400/60 px-2 py-0.5 rounded-full border border-emerald-500/18 bg-emerald-500/8">Arbitrum One</span>
                    </div>
                    {addrLoading ? (
                      <div className="h-9 rounded-xl bg-white/5 animate-pulse" />
                    ) : walletAddr ? (
                      <div className="flex items-center gap-2">
                        <p className="flex-1 font-mono text-[12px] text-white/55 break-all leading-relaxed">{walletAddr}</p>
                        <button onClick={copyAddr}
                          className={`shrink-0 px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase transition-colors ${
                            copied ? "border-emerald-400/20 bg-emerald-500/10 text-emerald-400" : "border-white/10 bg-white/5 text-white/35 hover:bg-white/10"
                          }`}>
                          {copied ? "Copied!" : "Copy"}
                        </button>
                      </div>
                    ) : (
                      <p className="text-xs text-white/25">Wallet initializing…</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    {[
                      { n: "1", text: "Buy USDC on any exchange — Coinbase, Binance, or Kraken" },
                      { n: "2", text: "Bridge or withdraw to the Arbitrum One network" },
                      { n: "3", text: "Send USDC to your address above — $10 minimum recommended" },
                    ].map(item => (
                      <div key={item.n} className="flex items-start gap-3">
                        <span className="shrink-0 h-5 w-5 rounded-full bg-white/6 border border-white/10 text-[9px] font-black text-white/35 flex items-center justify-center mt-0.5">{item.n}</span>
                        <p className="text-[12px] text-white/40 leading-relaxed">{item.text}</p>
                      </div>
                    ))}
                  </div>

                  <div className="rounded-xl border border-amber-400/12 bg-amber-400/5 px-4 py-2.5">
                    <p className="text-[11px] text-amber-300/55 leading-snug">Only send USDC on the Arbitrum One network. Sending on other networks will result in loss of funds.</p>
                  </div>

                  <motion.button
                    onClick={() => advance()}
                    whileHover={{ scale: 1.015 }} whileTap={{ scale: 0.975 }}
                    className="w-full h-13 rounded-2xl bg-emerald-500 text-black text-[14px] font-black uppercase tracking-[0.08em]">
                    I&apos;ve Got It
                  </motion.button>

                  <button onClick={() => advance()} className="w-full text-center text-[11px] text-white/20 hover:text-white/40 transition-colors py-1">
                    I&apos;ll deposit later
                  </button>
                </div>
              )}

              {/* ── STEP 3: DONE ── */}
              {step === 3 && (
                <div className="space-y-5 text-center">
                  <div className="py-4">
                    <motion.div
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.05 }}
                      className="mx-auto mb-5 h-16 w-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                      <motion.div
                        initial={{ scale: 0 }} animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 400, damping: 22, delay: 0.15 }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                          <path d="M5 12l5 5L20 7" stroke="#34d399" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </motion.div>
                    </motion.div>
                    <h2 className="text-2xl font-black text-white tracking-tight">You&apos;re all set!</h2>
                    <p className="text-sm text-white/35 mt-2 leading-relaxed">
                      Welcome to BlackDoor. Your public profile is live — start predicting on real-world events.
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-left">
                    {[
                      { label: "Profile",  value: "Public" },
                      { label: "Network",  value: "Arbitrum" },
                      { label: "Fee",      value: "2%" },
                    ].map(s => (
                      <div key={s.label} className="rounded-xl border border-white/6 bg-white/2 p-3">
                        <p className="text-[9px] font-black uppercase tracking-widest text-white/20 mb-1">{s.label}</p>
                        <p className="text-sm font-black text-white/70">{s.value}</p>
                      </div>
                    ))}
                  </div>

                  <motion.button
                    onClick={handleDone}
                    whileHover={{ scale: 1.015 }} whileTap={{ scale: 0.975 }}
                    className="w-full h-13 rounded-2xl bg-emerald-500 text-black text-[14px] font-black uppercase tracking-[0.08em]">
                    Start Trading
                  </motion.button>
                </div>
              )}

            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
